import { html, unsafeCSS, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import EkoElement from "@/utilities/element";
import {
  createUploadFileItem,
  createExistingFileItem,
  formatFileSize,
  type UploadFileItem,
  type ExistingFile,
} from "./types";
import style from "./upload.css?inline";
import "@/components/featured-icon";
import "./upload-item";

@customElement("eko-upload")
class Upload extends EkoElement {
  static formAssociated = true;
  static styles = [...EkoElement.styles, unsafeCSS(style)];

  private _dragCounter: number = 0;

  @state()
  private _dragOver: boolean = false;

  private _existingFilesProcessed: boolean = false;

  @state()
  private _files: UploadFileItem[] = [];

  private handleDragEnter = (e: DragEvent) => {
    if (!this.canAcceptDrop) return;
    e.preventDefault();
    e.stopPropagation();

    // Increment counter on drag enter
    this._dragCounter++;

    if (!this._dragOver) {
      this._dragOver = true;
      this.dispatchEvent(
        new CustomEvent("dragover-change", {
          detail: { dragOver: true },
          bubbles: true,
          composed: true,
        })
      );
    }
  };

  private handleDragLeave = (e: DragEvent) => {
    if (!this.canAcceptDrop) return;
    e.preventDefault();
    e.stopPropagation();

    // Use a counter to handle nested elements properly
    this._dragCounter--;

    // Only set dragOver to false when we've left all nested elements
    if (this._dragCounter === 0) {
      this._dragOver = false;
      this.dispatchEvent(
        new CustomEvent("dragover-change", {
          detail: { dragOver: false },
          bubbles: true,
          composed: true,
        })
      );
    }
  };

  private handleDragOver = (e: DragEvent) => {
    if (!this.canAcceptDrop) return;
    e.preventDefault();
    e.stopPropagation();
  };

  private handleDrop = (e: DragEvent) => {
    if (!this.canAcceptDrop) return;
    e.preventDefault();
    e.stopPropagation();
    this._dragOver = false;
    this._dragCounter = 0; // Reset counter on drop

    this.dispatchEvent(
      new CustomEvent("dragover-change", {
        detail: { dragOver: false },
        bubbles: true,
        composed: true,
      })
    );

    const files = Array.from(e.dataTransfer?.files || []);
    this.processFiles(files);
  };

  private handleDropzoneClick = () => {
    if (this.disabled) return;
    this.slottedInput?.click();
  };

  private handleDropzoneKeydown = (e: KeyboardEvent) => {
    if (this.disabled) return;
    // Activate on Enter or Space key
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      this.slottedInput?.click();
    }
  };
  private internals: ElementInternals;
  private slottedInput?: HTMLInputElement;
  private slottedInputName?: string;

  @property({ reflect: true })
  accept?: string;

  @property({ reflect: true, type: Boolean })
  disabled: boolean = false;

  @property({ type: Array, attribute: "existing-files" })
  existingFiles?: ExistingFile[];

  @property({ reflect: true, type: Number, attribute: "max-files" })
  maxFiles?: number;

  @property({ reflect: true, type: Number, attribute: "max-file-size" })
  maxFileSize?: number;

  @property({ reflect: true, type: Boolean })
  multiple: boolean = false;

  constructor() {
    super();
    // Attach internals to enable form-associated functionality
    this.internals = this.attachInternals();
  }

  protected firstUpdated() {
    this.handleInputSlotChange();
    this.processExistingFiles();
  }

  private getAcceptHint(): string {
    if (!this.accept) return "";
    const acceptTypes = this.accept.split(",").map((t) => t.trim());
    const formatted = acceptTypes.map((type) => {
      // Handle MIME types like image/*, video/*, etc.
      if (type.endsWith("/*")) {
        const category = type.split("/")[0];
        return category.charAt(0).toUpperCase() + category.slice(1);
      }
      // Handle file extensions like .pdf, .docx
      if (type.startsWith(".")) {
        return type.substring(1).toUpperCase();
      }
      // Handle full MIME types like image/png
      if (type.includes("/")) {
        const ext = type.split("/")[1];
        return ext.toUpperCase();
      }
      return type.toUpperCase();
    });

    // Format the list nicely
    if (formatted.length === 1) {
      return formatted[0];
    } else if (formatted.length === 2) {
      return `${formatted[0]} or ${formatted[1]} only`;
    } else {
      const allButLast = formatted.slice(0, -1).join(", ");
      return `${allButLast} or ${formatted[formatted.length - 1]} only`;
    }
  }

  private handleFileInputChange() {
    if (!this.slottedInput?.files) return;
    const filesArray = Array.from(this.slottedInput.files);
    this.processFiles(filesArray);
  }

  private handleFileRemove(e: CustomEvent) {
    const { fileName } = e.detail;
    const index = this._files.findIndex((f) => f.name === fileName);
    if (index === -1) return;

    const file = this._files[index];
    const isExisting = file.status === "existing";
    this._files = this._files.filter((_, i) => i !== index);

    this.dispatchEvent(
      new CustomEvent("file-removed", {
        detail: { file, index, isExisting },
        bubbles: true,
        composed: true,
      })
    );

    // In single-file mode, clear the input completely if no files remain
    if (!this.multiple && this._files.length === 0 && this.slottedInput) {
      this.slottedInput.value = "";
    }

    // Sync the native input to maintain form compatibility
    this.syncInputFiles();
  }

  private handleInputSlotChange() {
    const input = this.querySelector('input[type="file"]') as HTMLInputElement | null;
    if (!input) return;

    this.slottedInput = input;
    input.onchange = () => this.handleFileInputChange();

    // Preserve the original name attribute if it exists
    if (input.name) {
      this.slottedInputName = input.name;
    }

    // Sync properties
    if (this.multiple) input.multiple = true;
    if (this.accept) input.accept = this.accept;
    if (this.disabled) input.disabled = true;
  }

  private processExistingFiles() {
    if (this._existingFilesProcessed || !this.existingFiles || this.existingFiles.length === 0)
      return;

    // In single-file mode, only use the first existing file
    const filesToAdd = this.multiple ? this.existingFiles : this.existingFiles.slice(0, 1);

    const uploadItems = filesToAdd.map((file) => createExistingFileItem(file));
    this._files = [...this._files, ...uploadItems];
    this._existingFilesProcessed = true;
  }

  private processFiles(files: File[]) {
    const validFiles: UploadFileItem[] = [];
    const invalidFiles: UploadFileItem[] = [];
    const reasons: { [id: string]: string[] } = {};

    // If not multiple, only allow one file total and replace existing files
    if (!this.multiple) {
      // Clear existing files if single file mode
      this._files = [];

      // Only take the first file
      if (files.length > 1) {
        // Reject all files after the first
        files.slice(1).forEach((file) => {
          const uploadFile = createUploadFileItem(file);
          uploadFile.status = "error";
          uploadFile.error = "Only one file allowed";
          reasons[uploadFile.id] = ["Only one file allowed (multiple files not enabled)"];
          invalidFiles.push(uploadFile);
        });
      }
      files = files.slice(0, 1);
    }

    // Check max files limit
    const remainingSlots = this.maxFiles ? this.maxFiles - this._files.length : Infinity;
    const filesToProcess = files.slice(0, remainingSlots);
    const rejectedDueToLimit = files.slice(remainingSlots);

    // Handle files rejected due to max files limit
    rejectedDueToLimit.forEach((file) => {
      const uploadFile = createUploadFileItem(file);
      uploadFile.status = "error";
      uploadFile.error = `Maximum file limit reached (${this.maxFiles} files)`;
      reasons[uploadFile.id] = [`Maximum file limit reached (${this.maxFiles} files)`];
      invalidFiles.push(uploadFile);
    });

    filesToProcess.forEach((file) => {
      const uploadFile = createUploadFileItem(file);
      const fileReasons: string[] = [];

      // Validate file size
      if (this.maxFileSize && file.size > this.maxFileSize) {
        fileReasons.push(
          `File size (${formatFileSize(file.size)}) exceeds maximum of ${formatFileSize(this.maxFileSize)}`
        );
      }

      // Validate file type
      if (this.accept) {
        const acceptedTypes = this.accept.split(",").map((t) => t.trim());
        const isAccepted = acceptedTypes.some((acceptType) => {
          if (acceptType.startsWith(".")) {
            return file.name.toLowerCase().endsWith(acceptType.toLowerCase());
          }
          if (acceptType.endsWith("/*")) {
            const baseType = acceptType.split("/")[0].toLowerCase();
            return file.type.toLowerCase().startsWith(baseType + "/");
          }
          // Normalize both sides for case-insensitive comparison
          return file.type.toLowerCase() === acceptType.toLowerCase();
        });

        if (!isAccepted) {
          fileReasons.push(`File type not accepted. Accepted types: ${this.accept}`);
        }
      }

      if (fileReasons.length > 0) {
        uploadFile.status = "error";
        uploadFile.error = fileReasons[0];
        reasons[uploadFile.id] = fileReasons;
        invalidFiles.push(uploadFile);
      } else {
        validFiles.push(uploadFile);
      }
    });

    // Add all files (valid and invalid) to the list
    this._files = [...this._files, ...validFiles, ...invalidFiles];

    // Emit events
    if (validFiles.length > 0 || invalidFiles.length > 0) {
      this.dispatchEvent(
        new CustomEvent("files-added", {
          detail: { files: validFiles },
          bubbles: true,
          composed: true,
        })
      );
    }

    if (invalidFiles.length > 0) {
      this.dispatchEvent(
        new CustomEvent("files-rejected", {
          detail: { files: invalidFiles, reasons },
          bubbles: true,
          composed: true,
        })
      );
    }

    // Sync the native input to maintain form compatibility
    // This keeps only valid files in the input for native form submission
    this.syncInputFiles();
  }

  private syncFormValue() {
    // Only sync files that don't have errors and have a File object
    // Existing files don't have a File object, so they're excluded from form value
    const validFiles = this._files.filter(
      (f) => f.status !== "error" && f.status !== "existing" && f.file
    );

    // Set the form value via ElementInternals for proper form integration
    if (validFiles.length === 0) {
      this.internals.setFormValue(null);
    } else if (validFiles.length === 1 && validFiles[0].file) {
      // For single files, just pass the file directly
      this.internals.setFormValue(validFiles[0].file);
    } else {
      // For multiple files, use FormData
      const formData = new window.FormData();
      validFiles.forEach((item) => {
        if (item.file) {
          formData.append("files", item.file);
        }
      });
      this.internals.setFormValue(formData);
    }
  }

  private syncInputFiles() {
    if (!this.slottedInput) return;

    // Only sync files that don't have errors and have a File object
    // Existing files don't have a File object, so they're excluded from native input
    const validFiles = this._files.filter(
      (f) => f.status !== "error" && f.status !== "existing" && f.file
    );

    const dataTransfer = new window.DataTransfer();
    validFiles.forEach((item) => {
      if (item.file) {
        dataTransfer.items.add(item.file);
      }
    });
    this.slottedInput.files = dataTransfer.files;

    // Also sync the form value
    this.syncFormValue();
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("dragenter", this.handleDragEnter);
    this.addEventListener("dragover", this.handleDragOver);
    this.addEventListener("dragleave", this.handleDragLeave);
    this.addEventListener("drop", this.handleDrop);
  }

  disconnectedCallback() {
    this.removeEventListener("dragenter", this.handleDragEnter);
    this.removeEventListener("dragover", this.handleDragOver);
    this.removeEventListener("dragleave", this.handleDragLeave);
    this.removeEventListener("drop", this.handleDrop);
    super.disconnectedCallback();
  }

  formAssociatedCallback() {
    // Sync initial state when associated with a form
    this.syncFormValue();
  }

  formDisabledCallback(disabled: boolean) {
    this.disabled = disabled;
  }

  formResetCallback() {
    // Clear the file list when form is reset
    this._files = [];
    this._dragOver = false;
    this.syncFormValue();
    this.requestUpdate();
  }

  render() {
    // Hide dropzone in single-file mode when a file is already present
    const showDropzone = this.multiple || this._files.length === 0;

    return html`
      <div class="eko-upload" part="root" ?disabled=${this.disabled}>
        ${showDropzone
          ? html`
              <div
                class="eko-upload__dropzone"
                part="dropzone"
                role="button"
                tabindex=${this.disabled ? -1 : 0}
                aria-label="Upload file dropzone. Click to select files or drag and drop files here"
                aria-disabled=${this.disabled}
                ?data-dragover=${this._dragOver}
                @click=${this.handleDropzoneClick}
                @keydown=${this.handleDropzoneKeydown}>
                <div class="eko-upload__dropzone-content" part="dropzone-content">
                  <eko-featured-icon
                    icon="upload-cloud-02"
                    size="md"
                    variant="square-modern"></eko-featured-icon>
                  <div class="eko-upload__text">
                    <div class="eko-upload__action">
                      <span class="eko-upload__link">Click to upload</span>
                      <span class="eko-upload__or">or drag and drop</span>
                    </div>
                    <slot name="hint">
                      ${this.accept
                        ? html`<p class="eko-upload__hint">${this.getAcceptHint()}</p>`
                        : ""}
                    </slot>
                  </div>
                </div>
                <slot @slotchange=${this.handleInputSlotChange}></slot>
              </div>
            `
          : html`<div hidden><slot @slotchange=${this.handleInputSlotChange}></slot></div>`}
        ${this._files.length > 0
          ? html`
              <div class="eko-upload__file-list" part="file-list">
                <slot name="file-list">
                  ${this._files.map(
                    (file) => html`
                      <eko-upload-item
                        fileName=${file.name}
                        fileSize=${file.size}
                        file-type=${file.type}
                        file-url=${file.url || ""}
                        progress=${file.progress}
                        status=${file.status}
                        error=${file.error || ""}
                        removable
                        @remove=${this.handleFileRemove}></eko-upload-item>
                    `
                  )}
                </slot>
              </div>
            `
          : ""}
      </div>
    `;
  }

  public setFileComplete(fileId: string) {
    const file = this._files.find((f) => f.id === fileId);
    if (!file) return;

    file.progress = 100;
    file.status = "success";
    this.requestUpdate();
  }

  public setFileError(fileId: string, error: string) {
    const file = this._files.find((f) => f.id === fileId);
    if (!file) return;

    file.status = "error";
    file.error = error;
    this.requestUpdate();
  }

  updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);

    // Handle existingFiles property changes
    if (changedProperties.has("existingFiles") && this._existingFilesProcessed) {
      this._files = []; // Reset the files state to ensure duplicates are not retained

      // Add new existing files
      if (this.existingFiles && this.existingFiles.length > 0) {
        const filesToAdd = this.multiple ? this.existingFiles : this.existingFiles.slice(0, 1);
        const uploadItems = filesToAdd.map((file) => createExistingFileItem(file));
        this._files = [...uploadItems, ...this._files];
      }
    }

    // Sync property changes to the slotted input
    if (this.slottedInput) {
      if (changedProperties.has("accept")) {
        if (this.accept) {
          this.slottedInput.accept = this.accept;
        } else {
          this.slottedInput.removeAttribute("accept");
        }
      }

      if (changedProperties.has("multiple")) {
        this.slottedInput.multiple = this.multiple;
      }

      if (changedProperties.has("disabled")) {
        this.slottedInput.disabled = this.disabled;
      }

      // Preserve the name attribute if it was originally set
      if (this.slottedInputName && this.slottedInput.name !== this.slottedInputName) {
        this.slottedInput.name = this.slottedInputName;
      }
    }
  }

  public updateFileProgress(fileId: string, progress: number) {
    const file = this._files.find((f) => f.id === fileId);
    if (!file) return;

    file.progress = Math.min(100, Math.max(0, progress));
    file.status = file.progress === 100 ? "success" : "uploading";
    this.requestUpdate();
  }

  /**
   * Determines if drag and drop should be allowed.
   * Returns false if disabled or if in single file mode with a file already present.
   */
  private get canAcceptDrop(): boolean {
    if (this.disabled) return false;
    // In single file mode, don't allow drops if a file is already present
    if (!this.multiple && this._files.length > 0) return false;
    return true;
  }

  get files(): UploadFileItem[] {
    return this._files;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "eko-upload": Upload;
  }
}

export default Upload;
